import React, { useEffect, useState } from 'react';
import {
    Modal,
    IconButton,
    Tooltip,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    OutlinedInput,
    SelectChangeEvent,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { AllocationRule, PostingClass, PostingType } from '../../interfaces/accounting.interface';
import { AllocationRuleService } from '../../services/allocation-rule.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import ScrollableTable from '../common/ScrollableTable';
import { humanizeEnum } from '../../utils/format';

const POSTING_TYPE_LABELS: Record<PostingType, string> = {
    MANUAL_GL: 'Manual GL',
    CASH_TRANSACTION: 'Cash Transaction',
    VENDOR_INVOICE: 'Vendor Invoice',
    GOODS_RECEIPT: 'Goods Receipt',
    INVENTORY_ADJUSTMENT: 'Inventory Adjustment',
    EXPENSE_ACCRUAL: 'Expense Accrual',
    PAYMENT: 'Payment',
    SALARY: 'Salary',
    REVERSAL: 'Reversal',
    CUSTOMER_INVOICE: 'Customer Invoice',
    CUSTOMER_RECEIPT: 'Customer Receipt',
    FIXED_ASSET_ACQUISITION: 'Fixed Asset Acquisition',
    DEPRECIATION_ACCRUAL: 'Depreciation Accrual',
    ASSET_DISPOSAL: 'Asset Disposal',
    EXPORT_REALIZATION: 'Export Realization',
    WPPF_PROVISION: 'WPPF Provision',
};

const POSTING_CLASS_OPTIONS: PostingClass[] = [
    'BALANCE_SHEET',
    'RECEIVABLE',
    'PAYABLE',
    'CASH',
    'TAX',
    'EXPENSE',
    'REVENUE',
    'INVENTORY',
    'GR_IR_CLEARING',
    'INTER_SEGMENT_CLEARING',
];

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd',
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd',
    verticalAlign: 'middle' as const,
};

const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 560,
    backgroundColor: '#fff',
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    padding: '32px',
    borderRadius: '8px',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
};

const readOnlyFieldStyle = {
    marginBottom: '12px',
};

const readOnlyLabelStyle = {
    fontSize: '12px',
    color: '#888',
    marginBottom: '4px',
};

const readOnlyValueStyle = {
    fontSize: '14px',
    fontWeight: 600 as const,
    color: '#333',
};

const AllocationRuleList: React.FC = () => {
    const toast = useToast();
    const isInstitutionAdmin = usePermission(PERMISSIONS.ALLOCATION_RULE_MANAGE);

    const [rules, setRules] = useState<AllocationRule[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRule, setSelectedRule] = useState<AllocationRule | null>(null);
    const [editBasisClasses, setEditBasisClasses] = useState<PostingClass[]>([]);
    const [editAllocatableClasses, setEditAllocatableClasses] = useState<PostingClass[]>([]);
    const [saving, setSaving] = useState(false);

    const fetchRules = async () => {
        try {
            setLoading(true);
            const data = await AllocationRuleService.getGlobalRules();
            setRules(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to load allocation rules');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpenEdit = (rule: AllocationRule) => {
        setSelectedRule(rule);
        setEditBasisClasses(rule.basisClasses ?? []);
        setEditAllocatableClasses(rule.allocatableClasses ?? []);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedRule(null);
    };

    const handleBasisClassChange = (event: SelectChangeEvent<PostingClass[]>) => {
        const value = event.target.value;
        setEditBasisClasses(typeof value === 'string' ? (value.split(',') as PostingClass[]) : (value as PostingClass[]));
    };

    const handleAllocatableClassChange = (event: SelectChangeEvent<PostingClass[]>) => {
        const value = event.target.value;
        setEditAllocatableClasses(typeof value === 'string' ? (value.split(',') as PostingClass[]) : (value as PostingClass[]));
    };

    const handleSave = async () => {
        if (!selectedRule || !selectedRule.id) return;
        setSaving(true);
        try {
            await AllocationRuleService.update(selectedRule.id, {
                ...selectedRule,
                basisClasses: editBasisClasses,
                allocatableClasses: editAllocatableClasses,
            });
            toast.showSuccess('Rule updated');
            handleCloseModal();
            await fetchRules();
        } catch (err) {
            toast.showApiError(err, 'Failed to update rule');
        } finally {
            setSaving(false);
        }
    };

    const renderClassChips = (classes: PostingClass[]) => {
        if (!classes || classes.length === 0) {
            return <span style={{ color: '#bbb' }}>—</span>;
        }
        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {classes.map(cls => (
                    <Chip key={cls} label={humanizeEnum(cls)} size="small" />
                ))}
            </div>
        );
    };

    if (loading) {
        return <div style={{ padding: '20px' }}>Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }} data-help-page="accounting.allocationRules.page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' , flexWrap: 'wrap', gap: '8px' }}>
                <h2>Allocation Rules</h2>
            </div>

            <div data-help-section="accounting.allocationRules.table">
            <ScrollableTable>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                    <tr>
                        <th data-help="accounting.allocationRules.col.postingType" style={tableHeaderStyle}>Posting Type</th>
                        <th data-help="accounting.allocationRules.col.variant" style={tableHeaderStyle}>Variant</th>
                        <th data-help="accounting.allocationRules.col.method" style={tableHeaderStyle}>Allocation Method</th>
                        <th data-help="accounting.allocationRules.col.basis" style={tableHeaderStyle}>Basis Classes</th>
                        <th data-help="accounting.allocationRules.col.allocatable" style={tableHeaderStyle}>Allocatable Classes</th>
                        {isInstitutionAdmin && <th data-help="accounting.allocationRules.col.actions" style={tableHeaderStyle}>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {rules.length === 0 ? (
                        <tr>
                            <td style={tableCellStyle} colSpan={isInstitutionAdmin ? 6 : 5}>
                                No allocation rules found.
                            </td>
                        </tr>
                    ) : (
                        rules.map(rule => (
                            <tr key={rule.id}>
                                <td style={tableCellStyle}>{POSTING_TYPE_LABELS[rule.postingType] ?? humanizeEnum(rule.postingType)}</td>
                                <td style={tableCellStyle}>{rule.variant ?? '—'}</td>
                                <td style={tableCellStyle}>{rule.allocationMethod ?? '—'}</td>
                                <td style={tableCellStyle}>{renderClassChips(rule.basisClasses)}</td>
                                <td style={tableCellStyle}>{renderClassChips(rule.allocatableClasses)}</td>
                                {isInstitutionAdmin && (
                                    <td style={tableCellStyle}>
                                        <Tooltip title="Edit">
                                            <IconButton data-help="accounting.allocationRules.edit" size="small" color="primary" aria-label="edit" onClick={() => handleOpenEdit(rule)}>
                                                <Edit />
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                )}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            </ScrollableTable>
            </div>

            {/* Edit Modal */}
            <Modal open={modalOpen} onClose={handleCloseModal}>
                <div data-help-page="accounting.allocationRules.form.page" style={modalStyle}>
                    <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Edit Allocation Rule</h2>

                    {selectedRule && (
                        <>
                            {/* Read-only identity fields */}
                            <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '6px' }}>
                                <div style={readOnlyFieldStyle}>
                                    <div style={readOnlyLabelStyle}>Posting Type</div>
                                    <div style={readOnlyValueStyle}>{POSTING_TYPE_LABELS[selectedRule.postingType] ?? humanizeEnum(selectedRule.postingType)}</div>
                                </div>
                                <div style={readOnlyFieldStyle}>
                                    <div style={readOnlyLabelStyle}>Variant</div>
                                    <div style={readOnlyValueStyle}>{selectedRule.variant ?? '—'}</div>
                                </div>
                                <div style={{ ...readOnlyFieldStyle, marginBottom: 0 }}>
                                    <div style={readOnlyLabelStyle}>Allocation Method</div>
                                    <div style={readOnlyValueStyle}>{selectedRule.allocationMethod ?? '—'}</div>
                                </div>
                            </div>

                            {/* Editable: Basis Classes */}
                            <FormControl data-help="accounting.allocationRules.form.field.basis" fullWidth margin="normal">
                                <InputLabel id="basis-classes-label">Basis Classes</InputLabel>
                                <Select<PostingClass[]>
                                    labelId="basis-classes-label"
                                    multiple
                                    value={editBasisClasses}
                                    onChange={handleBasisClassChange}
                                    input={<OutlinedInput label="Basis Classes" />}
                                    renderValue={(selected) => (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                            {(selected as PostingClass[]).map(cls => (
                                                <Chip key={cls} label={cls} size="small" />
                                            ))}
                                        </div>
                                    )}
                                >
                                    {POSTING_CLASS_OPTIONS.map(pc => (
                                        <MenuItem key={pc} value={pc}>
                                            {pc}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Editable: Allocatable Classes */}
                            <FormControl data-help="accounting.allocationRules.form.field.allocatable" fullWidth margin="normal">
                                <InputLabel id="allocatable-classes-label">Allocatable Classes</InputLabel>
                                <Select<PostingClass[]>
                                    labelId="allocatable-classes-label"
                                    multiple
                                    value={editAllocatableClasses}
                                    onChange={handleAllocatableClassChange}
                                    input={<OutlinedInput label="Allocatable Classes" />}
                                    renderValue={(selected) => (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                            {(selected as PostingClass[]).map(cls => (
                                                <Chip key={cls} label={cls} size="small" />
                                            ))}
                                        </div>
                                    )}
                                >
                                    {POSTING_CLASS_OPTIONS.map(pc => (
                                        <MenuItem key={pc} value={pc}>
                                            {pc}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                <Button data-help="accounting.allocationRules.form.cancel" onClick={handleCloseModal} disabled={saving}>Cancel</Button>
                                <Button data-help="accounting.allocationRules.form.save" variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                                    {saving ? 'Saving...' : 'Save'}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default AllocationRuleList;
